import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSEO } from "../../context/SEOContext";
import "./OrderLookup.css";
const OrderLookup = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [order, setOrder] = useState(null);

  const setSeo = useSEO();
  useEffect(() => {
    setSeo({
      title: t("orderDetail.meta.title"),
      description: t("orderDetail.meta.description"),
      keywords: t("orderDetail.meta.keywords"),
      favicon: "/faviconnew.png", // ✅ burada favicon tanımlandı
    });
  }, [setSeo, t]);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/orders?id=${id}`)
      .then((res) => res.json())
      .then((data) => {
        if (!data.error) {
          setOrder(data);
        } else {
          setOrder(null);
        }
      })
      .catch(() => setOrder(null));
  }, [id]);

  const getCurrencySymbol = (currency) => {
    switch (currency) {
      case "USD":
        return "$";
      case "EUR":
        return "€";
      case "TRY":
        return "₺";
      case "GBP":
        return "£";
      default:
        return currency;
    }
  };

  const formatPrice = (price, currency) => {
    const numericPrice = parseFloat(price);

    if (currency === "TRY") {
      return numericPrice.toLocaleString("tr-TR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    }

    const rounded = Math.round(numericPrice);
    return rounded.toLocaleString("en-US");
  };

  const formatDateTime = (datetime) => {
    const date = new Date(datetime);
    return `${date.getHours().toString().padStart(2, "0")}:${date
      .getMinutes()
      .toString()
      .padStart(2, "0")} / ${date.getDate().toString().padStart(2, "0")}.${(
      date.getMonth() + 1
    )
      .toString()
      .padStart(2, "0")}.${date.getFullYear()}`;
  };

  if (!order) {
    return (
      <div className="container py-5 text-center">
        <div className="alert alert-danger" role="alert">
          <h4 className="mb-1 mt-3">{t("OrderErrorTitle")}</h4>
          <p>{t("OrderErrorDesc")}</p>
        </div>
      </div>
    );
  }

  const address = order.shipping_address;

  const getLocalizedColorName = (item, t) => {
    const selected = item.selectedVariation?.name;
    const defaultColor = item.color?.split(",")[0]?.trim();

    const rawColor =
      selected && selected !== "Default" ? selected.trim() : defaultColor || "";

    if (!rawColor) return "";
    const normalized =
      rawColor.charAt(0).toUpperCase() + rawColor.slice(1).toLowerCase();

    return t(normalized);
  };

  const formatMoney = (amount, ccy) => {
    const n = Number(amount || 0);
    const code = (ccy || "TRY").toUpperCase() === "TL" ? "TRY" : ccy || "TRY";
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: code,
        minimumFractionDigits: code === "TRY" ? 0 : 0,
        maximumFractionDigits: code === "TRY" ? 0 : 0,
      }).format(n);
    } catch {
      return `${code} ${n.toFixed(code === "TRY" ? 0 : 0)}`;
    }
  };

  const getCustomizationAmount = (item, orderCurrency) => {
    const ccy = (orderCurrency || "TRY").toUpperCase();
    const qty = Number(item.quantity || 1);

    const directValue =
      item.customization_price != null
        ? Number(item.customization_price)
        : null;

    if (directValue != null && Number.isFinite(directValue)) {
      return directValue * (item.customization_enabled ? 1 : 0);
    }

    const priceByCurrency =
      item.customization_price_currency &&
      String(item.customization_price_currency).toUpperCase() === ccy
        ? Number(item.customization_price || 0)
        : null;

    if (priceByCurrency != null && Number.isFinite(priceByCurrency)) {
      return priceByCurrency * (item.customization_enabled ? 1 : 0);
    }

    if (ccy === "TRY") {
      const tryVal = Number(
        item.customization_price_try ?? item.price_try ?? 0,
      );
      if (Number.isFinite(tryVal)) return tryVal * qty;
    }

    if (item.customization?.enabled) {
      const customValue = Number(item.customization_price ?? 0);
      if (Number.isFinite(customValue)) return customValue * qty;
    }

    return 0;
  };

  // Satır tutarını işlem para biriminde hesapla
  const pickLineAmount = (item, order, items) => {
    const ccy = (order?.currency || "TRY").toUpperCase();
    const qty = Number(item.quantity || 1);
    const customizationAmount = getCustomizationAmount(item, ccy);

    // 1) Aynı para birimiyle kaydedilmişse
    if (item.currency && String(item.currency).toUpperCase() === ccy) {
      if (item.line_total != null) {
        return Number(item.line_total) + customizationAmount;
      }
      if (item.unit_price != null) {
        return Number(item.unit_price) * qty + customizationAmount;
      }
    }

    // 2) Tek ürünse: toplamı satıra yaz (en güvenlisi)
    if ((items?.length || 0) === 1) {
      return Number(order.total_price || 0);
    }

    // 3) Çok ürünse: TRY alanlarından oranlayarak dağıt
    const list = items || [];
    const sumTry = list.reduce(
      (s, it) =>
        s +
        Number(
          it.line_total_try ??
            it.price_try ??
            (it.currency?.toUpperCase() === "TRY"
              ? (it.line_total ?? it.price ?? 0)
              : 0),
        ),
      0,
    );
    if (sumTry > 0) {
      const selfTry =
        Number(item.line_total_try ?? item.price_try ?? 0) ||
        (item.currency?.toUpperCase() === "TRY"
          ? Number(item.line_total ?? item.price ?? 0)
          : 0);
      const ratio = selfTry / sumTry;
      return Number(order.total_price || 0) * ratio;
    }

    // 4) Son çare: elde ne varsa
    if (item.line_total != null)
      return Number(item.line_total) + customizationAmount;
    if (item.price != null) return Number(item.price) + customizationAmount;
    if (item.unit_price != null)
      return Number(item.unit_price) * qty + customizationAmount;

    return customizationAmount;
  };

  const shippingFee = Number(
    order?.shipping_fee ?? order?.shipping_amount ?? order?.shipping ?? 0,
  );
  const couponCode = order?.coupon_code ?? null;
  const couponDiscount = Number(order?.coupon_discount ?? 0);
  const couponUsed = !!couponCode && couponDiscount > 0;
  const customizationTotal = (order?.items || []).reduce(
    (sum, item) => sum + getCustomizationAmount(item, order?.currency),
    0,
  );

  return (
    <div className="container py-5">
      <div className="row">
        <div className="col-lg-12 mt-3">
          <div className="text-center border p-5 mb-5 rounded-2">
            <i className="fa-regular fa-circle-check fa-6x iconcolor"></i>
            <h3 className="mt-4 fw-bold">{t("OrderSuccessTitle")}</h3>
            <h6 className="mt-3">
              {t("OrderSuccessDesc")}
              <br />
              {t("OrderSuccessDesc2")}
            </h6>
          </div>
        </div>
        <div className="col-lg-12">
          <h2 className="mb-5 cardtitlecolor">{t("OrderDetailsTitle")}</h2>

          <div className="cardborder p-4 rounded-3">
            <div className="row p-4">
              <div className="col-lg-6 mt-3">
                <h5 className="mb-4 cardtitlecolor"> {t("BillingAddress")}</h5>
                <p className="mb-1 text-muted"> {t("EmailLabel")}</p>
                <h6 className="fw-normal mb-3">{order.email}</h6>

                <p className="mb-1 text-muted"> {t("PhoneNumberLabel")}</p>
                <h6 className="fw-normal mb-3">{address.phone}</h6>

                <p className="mb-1 text-muted"> {t("AddressLabel")}</p>
                <h6 className="fw-normal mb-3">
                  {address.address1} {address.address2}
                </h6>

                <p className="mb-1 text-muted"> {t("LocationLabel")}</p>
                <h6 className="fw-normal mb-4">
                  {address.country} / {address.city} / {address.state}
                </h6>

                <div className="d-flex justify-content-between mt-2">
                  <span className="text-muted">{t("Shipping")}</span>
                  <strong className="cardtitlecolor">
                    {formatMoney(shippingFee, order.currency)}
                  </strong>
                </div>

                <div className="d-flex justify-content-between mt-2">
                  <span className="text-muted">{t("Coupon") || "Coupon"}</span>
                  <strong className="cardtitlecolor">
                    {couponUsed
                      ? formatMoney(couponDiscount, order.currency)
                      : "-"}
                  </strong>
                </div>

                <hr />
                <div className="d-flex justify-content-between">
                  <h5 className="cardtitlecolor"> {t("TotalLabel")}</h5>
                  <h5 className="cardtitlecolor">
                    {getCurrencySymbol(order.currency)}
                    {formatPrice(order.total_price, order.currency)}
                  </h5>
                </div>
              </div>

              <div className="col-lg-6 mt-3">
                <h5 className="mb-4 cardtitlecolor">
                  {t("OrderSummaryTitle")}
                </h5>
                <div className="row text-center mb-4">
                  <div className="col-lg-4">
                    <p className="text-muted mb-1 text-start">
                      {t("OrderDate")}
                    </p>
                    <h5 className="fw-normal text-start">
                      {formatDateTime(order.order_date)}
                    </h5>
                  </div>
                  <div className="col-lg-4 col-md-4 col-sm-6 col-12 text-start">
                    <p className="text-muted mb-1"> {t("OrderID")}</p>
                    <h5 className="fw-normal">#{order.id}</h5>
                  </div>
                  <div className="col-lg-4 col-md-4 col-sm-6 col-12 text-start">
                    <p className="text-muted mb-1"> {t("StatusLabel")}</p>
                    <span
                      className="badge text-white fw-light p-2 pe-3 px-3"
                      style={{
                        fontSize: 13,
                        backgroundColor: "var(--cherry-color)",
                      }}
                    >
                      {order.status}
                    </span>
                  </div>
                </div>

                {order.items.map((item) => (
                  <div key={item.id} className="cardborder rounded-3 p-3 mb-2">
                    <div className="d-none d-sm-block">
                      <div className="d-flex align-items-center gap-3">
                        <img
                          src={item.image_url}
                          width={80}
                          height={80}
                          className="object-fit-cover"
                          alt={item.product_name}
                        />
                        <div className="d-flex justify-content-between align-items-center flex-grow-1">
                          <div>
                            <h6 className="cardtitlecolor">
                              {item.product_name}
                            </h6>
                            <div className="small text-muted">
                              <span className="me-2">
                                {t("ColorLabel")} :{" "}
                                {getLocalizedColorName(item, t)}
                              </span>
                              |
                              <span className="ms-2">
                                {t("QuantityLabel")} : {item.quantity}
                              </span>
                            </div>
                          </div>
                          <h5 className="mb-0 fw-semibold cardtitlecolor">
                            {formatMoney(
                              pickLineAmount(item, order, order.items),
                              order.currency,
                            )}
                          </h5>
                        </div>
                      </div>
                    </div>

                    <div className="d-block d-sm-none">
                      <div className="col-lg-12">
                        <img
                          src={
                            item.image_url ||
                            "https://mukemel.com/wp-content/uploads/2025/04/BTC0164-1200x1200.webp"
                          }
                          width={80}
                          height={80}
                          className="object-fit-cover"
                          alt={item.product_name}
                        />

                        <div className="fw-semibold">
                          <h6 className="cardtitlecolor mb-1 mt-2">
                            {item.product_name}
                          </h6>
                        </div>
                        <div className="small text-muted mb-1">
                          <span className="fw-14 me-1 ">
                            {t("ColorLabel")} : {getLocalizedColorName(item, t)}
                          </span>
                          |
                          <span className="mx-1 fw-14">
                            {t("QuantityLabel")} : {item.quantity}
                          </span>
                        </div>
                        <h5 className="mb-0 fw-semibold cardtitlecolor">
                          {formatMoney(
                            pickLineAmount(item, order, order.items),
                            order.currency,
                          )}
                        </h5>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="col-lg-12 fw-14">
                {customizationTotal > 0 && (
                  <div className="d-flex justify-content-between align-items-center mt-4 mb-2">
                    <span className="text-muted">
                      {t("customization.price")}
                    </span>
                    <strong>
                      {formatMoney(customizationTotal, order.currency)}
                    </strong>
                  </div>
                )}

                {/* Kupon bilgisi */}
                {couponUsed ? (
                  <div
                    className="alert alert-success d-flex align-items-center mt-4"
                    role="alert"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="currentColor"
                      className="bi bi-exclamation-triangle-fill flex-shrink-0 me-2"
                      viewBox="0 0 16 16"
                      role="img"
                      aria-label="Warning:"
                    >
                      <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" />
                    </svg>
                    <div>
                      <small>
                        {t("couponalert1")}{" "}
                        <strong className="text-uppercase">{couponCode}</strong>{" "}
                        {t("couponalert2")}
                        <strong>
                          {formatMoney(couponDiscount, order.currency)}
                        </strong>
                      </small>
                    </div>
                  </div>
                ) : (
                  <div
                    className="alert alert-danger d-flex align-items-center mt-4"
                    role="alert"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="currentColor"
                      className="bi bi-exclamation-triangle-fill flex-shrink-0 me-2"
                      viewBox="0 0 16 16"
                      role="img"
                      aria-label="Warning:"
                    >
                      <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" />
                    </svg>
                    <div>
                      <small> {t("couponalert3")}</small>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderLookup;
